import React, { useState, useEffect, useCallback } from "react";

export interface TableData {
  id: string | number;
  name: string;
  email?: string;
  phone?: string;
  [key: string]: any;
}

interface DataTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  fetchDataFn: () => Promise<{ body: TableData[] } | null>;
  onSelect?: (item: TableData) => void;
  title?: string;
}

const DataTableModal: React.FC<DataTableModalProps> = ({
  isOpen,
  onClose,
  fetchDataFn,
  onSelect,
  title = "Select Item",
}) => {
  const [data, setData] = useState<TableData[]>([]);
  const [filteredData, setFilteredData] = useState<TableData[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response:any = await fetchDataFn();
      if (response && Array.isArray(response.data)) {
        //   console.log(response.data,"table data")
        setData(response.data);
        setFilteredData(response.data);
      } else {
        setError("No data available");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [fetchDataFn]);
  

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, fetchData]);

  useEffect(() => {
    if (searchTerm === "") {
      setFilteredData(data);
    } else {
      const lower = searchTerm.toLowerCase();
      const filtered = data.filter(item =>
        item.name?.toLowerCase().includes(lower) ||
        item.email?.toLowerCase().includes(lower) ||
        item.phone?.toLowerCase().includes(lower)
      );
      setFilteredData(filtered);
    }
  }, [searchTerm, data]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center" onClick={onClose}></div>
      <div className="fixed top-1/2 left-1/2 z-50 w-[90%] max-w-3xl transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            &times;
          </button>
        </div>

        <input
          type="text"
          placeholder="Search..."
          className="w-full px-4 py-2 border rounded-md mb-4"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {loading ? (
          <div className="text-center py-6">Loading...</div>
        ) : error ? (
          <div className="text-center text-red-500">{error}</div>
        ) : (
          <div className="max-h-[50vh] overflow-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-4 py-2">Booking Name</th>
                  <th className="px-4 py-2">Booking Number</th>
                  {/* <th className="px-4 py-2">Booking Type</th> */}
                  <th className="px-4 py-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 border-b">
                      <td className="px-4 py-2">{item.bookingName || "N/A"}</td>
                      <td className="px-4 py-2">{item.bookingNumber || "N/A"}</td>
                      {/* <td className="px-4 py-2">{item.bookingtype || "N/A"}</td> */}
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => {
                            onSelect?.(item);
                            onClose();
                          }}
                          className="bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600"
                        >
                          Select
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-4 text-gray-500">
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default DataTableModal;
